import { computed, inject, Injectable, signal } from '@angular/core';
import { Auth, onAuthStateChanged, User } from '@angular/fire/auth';
import { customClaims } from './custom-claims';
import { Router } from '@angular/router';
import { LoggerService } from '../../../../shared/services/logger.service';
import { AuthApiService } from '../auth-api.service';
import { StartPrintUserDto } from '../../models/server-models/entities/startprint-user-dto';
import { ErrorService } from '../../../../shared/services/error.service';
import { HttpErrorResponse } from '@angular/common/http';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  auth = inject(Auth);
  router = inject(Router);
  loggerService = inject(LoggerService);
  authApiService = inject(AuthApiService);
  errorService = inject(ErrorService);
  printOfficeId = signal<string | null>(null);
  isUserProvider = computed(() => !!this.printOfficeId());
  startPrintUser = signal<StartPrintUserDto | null>(null);

  shouldLoadStartPrintUser = true;

  constructor() {
    console.log('auth inited');
    this.subscribeOnAuthChanged();
  }

  tryGetUser(): User | null {
    return this.auth.currentUser;
  }

  isAuthenticated(): boolean {
    return !!this.tryGetUser();
  }

  changeUserPhone(phone: string) {
    this.startPrintUser.set({
      id: this.startPrintUser()!.id,
      email: this.startPrintUser()!.email,
      mobilePhone: phone,
      displayName: this.startPrintUser()!.displayName,
    });
  }

  async signOut() {
    this.loggerService.logEvent('UserLoggedOut');

    await FirebaseAuthentication.signOut;
    await this.auth.signOut();
  }

  private subscribeOnAuthChanged() {
    onAuthStateChanged(this.auth, async (user) => {
      this.loggerService.setUserContext(user?.uid, user?.email || '');

      if (!user) {
        this.router.navigate(['/login']);
        this.startPrintUser.set(null);
        return;
      }

      // This is done to avoid loading user before auth token is saved
      // It is used to handle case when user is signin in (so in that case, sign-in component will load user)
      if (this.shouldLoadStartPrintUser) {
        this.loadStartPrintUser();
      }

      this.loggerService.logEvent('UserLoggedIn');

      const officeId = await this.getPrintOfficeIdFromClaims();

      this.printOfficeId.set(officeId);
      console.log('office id:', officeId);
      console.log('user:', user);
    });
  }

  private loadStartPrintUser() {
    this.authApiService.getStartPrintUser().subscribe({
      next: (user) => {
        this.startPrintUser.set(user);
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.handleError(error);
      },
    });
  }
  private async getPrintOfficeIdFromClaims(): Promise<string | null> {
    const currentUser = this.tryGetUser();

    if (!currentUser) return null;

    const idToken = await currentUser.getIdTokenResult();

    return idToken.claims[customClaims.printOfficeId] as string;
  }
}
